import { useQueryClient, useMutation, useQuery } from '@tanstack/react-query';
import { type FormEvent, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, ApiError } from '../api/client';
import { Card } from '../components/ui/Card';
import { Spinner } from '../components/ui/Spinner';

export function Sites() {
  const queryClient = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [name, setName] = useState('');
  const [domain, setDomain] = useState('');
  const [error, setError] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['sites'],
    queryFn: () => api.listSites(),
  });

  const createSite = useMutation({
    mutationFn: () => api.createSite({ name, domain }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sites'] });
      setName('');
      setDomain('');
      setShowForm(false);
      setError(null);
    },
    onError: (err) => setError(err instanceof ApiError ? err.message : 'Anlegen fehlgeschlagen.'),
  });

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    createSite.mutate();
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold text-slate-900 dark:text-white">Properties</h1>
        <button
          onClick={() => setShowForm((v) => !v)}
          className="rounded-lg bg-brand-600 px-3 py-2 text-sm font-medium text-white transition-colors hover:bg-brand-700"
        >
          {showForm ? 'Abbrechen' : '+ Neue Property'}
        </button>
      </div>

      {showForm && (
        <Card title="Neue Property anlegen">
          <form onSubmit={handleSubmit} className="flex flex-wrap items-end gap-4">
            <div>
              <label htmlFor="site-name" className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300">
                Name
              </label>
              <input
                id="site-name"
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Kunde GmbH"
                className="w-56 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 dark:border-slate-700 dark:bg-slate-800 dark:text-white"
              />
            </div>
            <div>
              <label htmlFor="site-domain" className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300">
                Domain
              </label>
              <input
                id="site-domain"
                required
                value={domain}
                onChange={(e) => setDomain(e.target.value)}
                placeholder="kunde.de"
                className="w-56 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 dark:border-slate-700 dark:bg-slate-800 dark:text-white"
              />
            </div>
            <button
              type="submit"
              disabled={createSite.isPending}
              className="rounded-lg bg-brand-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-brand-700 disabled:opacity-50"
            >
              {createSite.isPending ? 'Anlegen…' : 'Anlegen'}
            </button>
          </form>
          {error && <p className="mt-3 text-sm text-rose-500">{error}</p>}
        </Card>
      )}

      <Card>
        {isLoading ? (
          <div className="flex justify-center py-8">
            <Spinner />
          </div>
        ) : !data || data.sites.length === 0 ? (
          <p className="py-8 text-center text-sm text-slate-400">Noch keine Properties angelegt.</p>
        ) : (
          <div className="divide-y divide-slate-100 dark:divide-slate-800/60">
            {data.sites.map((site) => (
              <Link
                key={site.id}
                to={`/sites/${site.id}`}
                className="flex items-center justify-between px-1 py-3 hover:bg-slate-50 dark:hover:bg-slate-800/40"
              >
                <div>
                  <p className="font-medium text-slate-900 dark:text-white">{site.name}</p>
                  <p className="text-xs text-slate-500 dark:text-slate-400">{site.domain}</p>
                </div>
                <span className="text-brand-600 dark:text-brand-400">→</span>
              </Link>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}
